import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
// SSOT incident type (from @aica/api-types/schemas, re-exported via
// alerts.types.ts). Every method below shares it — there used to be a
// second, hand-written `Incident` shape here for the read/update methods
// that diverged from the DB row on four counts (a nonexistent `orgId`,
// `analysisId`/`recordId`/`projectId` wrongly optional, a missing
// `createdByUserId`/`updatedAt`, and a `status` union missing
// `in_progress`/`closed` while claiming the alert-only `acknowledged`).
// See the B8 finding in the 2026-07-27 alerts follow-up report.
import type { Incident, CreateIncidentRequest } from '../types';

export class IncidentsApi extends BaseApiClient {
  /**
   * An alert fires on its own; an incident is opened deliberately by a
   * person. Moved from `alerts.createIncident` — same endpoint, same
   * request/response shape, only the domain changed.
   */
  async create(data: CreateIncidentRequest): Promise<Incident> {
    return this.request<Incident>(API_ENDPOINTS.INCIDENTS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async list(params?: {
    status?: string;
    severity?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ incidents: Incident[]; total: number }> {
    const query = this.buildQuery(params);
    return this.request(`${API_ENDPOINTS.INCIDENTS}${query}`);
  }

  async getById(id: string): Promise<{ incident: Incident }> {
    return this.request(API_ENDPOINTS.INCIDENTS_BY_ID(id));
  }

  async update(
    id: string,
    data: Partial<Pick<Incident, 'title' | 'description' | 'severity' | 'status'>>
  ): Promise<{ incident: Incident }> {
    return this.request(API_ENDPOINTS.INCIDENTS_BY_ID(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async resolve(id: string): Promise<{ incident: Incident }> {
    return this.request(API_ENDPOINTS.INCIDENTS_RESOLVE(id), {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }
}
